import { API } from '../api';
import { JSONtoCSV } from '../consts';
import { CopyOutlined } from '@ant-design/icons';
import {isEmpty} from 'lodash';
import { notification } from 'antd';
import { saveAs } from 'file-saver';
import { Link } from "react-router-dom";
import JSZip from 'jszip';
import React from 'react';
import axios from 'axios';

export async function _getMappingData(
  mapping_client_id,
  version,
  resourcetype,
  namespace,
  file = null,
) {
  const params = {
    client_id: mapping_client_id,
    version,
    resourcetype,
    namespace: isEmpty(namespace) ? 'default' : namespace,
  };

  if (file) {
    params.file = file;
  }

  return await API.get('/mapping', { params });
}

export async function getGroovyFuncs(params) {
  const res = await API.get('/stdlib', { params });
  let data = res.data.stdlib;
  data = data.map(func => {
    func.key = func.id;
    return func;
  });
  return data;
}

/**
 * Returns an array of all groovy functions found in a given string.
 * Returns null when no groovy functions are found.
 * @param {string} str
 * @param {boolean} firstMatchOnly
 *  See tests for examples
 */
export function getGroovyFuncNamesFromStr(str, firstMatchOnly = false) {
  if (str == null) return null
  const groovyRegex = /([a-zA-Z_{1}.][a-zA-Z0-9_]+)(?=\()/gm;
  const matches = str.trim().replace('static def').match(groovyRegex);
  const results = matches ? matches.filter(x => !x.startsWith('.')) : matches;

  if (results && results.length > 0) {
    return firstMatchOnly ? results[0] : results;
  }

  return null;
}

function recurse(cur, prop, result) {
  if (Object(cur) !== cur) {
    result[prop] = cur;
  } else if (Array.isArray(cur)) {
    const l = cur.length;
    for (let i = 0; i < l; i++) {
      recurse(cur[i], prop ? `${prop}[${i}]` : `${i}`, result);
    }

    if (l === 0) {
      result[prop] = [];
    }
  } else {
    let isEmpty = true;

    for (const p in cur) {
      isEmpty = false;
      recurse(cur[p], prop ? `${prop}.${p}` : p, result);
    }

    if (isEmpty) {
      result[prop] = {};
    }
  }
}

export function flattenJson(data) {
  const result = {};
  recurse(data, '', result);
  return result;
}

/**
 * Returns how the record value should be rendered in the table.
 * @param {*} record
 * @param key
 */
export function renderRecord(record, key) {
  if (record[key] === undefined) return;
  if (record[key] === null) return <code>null</code>;

  if (isCode(record[key], record.path)) {
    return <code className="code-green">{record[key]}</code>;
  }

  return record[key];
}

/**
 * Returns filtered validation results
 * Made to filter out invalid eob adjudication errors
 */
export function handleFhirValidation(validationResults, fhirResource) {
  let updatedResults = validationResults;
  let adjudicationsToValidate = [];

  for (const [index,issue] of validationResults.issue.entries()) {
    if (issue.severity === 'error') {
      if (issue.expression[0] && issue.expression[0].includes('adjudication') ) {
        adjudicationsToValidate.unshift({
          resource: issue.expression[0].replace('ExplanationOfBenefit.', ''),
          errorIndex: index
      })
      }
    }
  }

  for (const adj of adjudicationsToValidate) {
    let itemIndex = null;
    let adjIndex = null;
    let adjudication = null;
    let res = adj.resource.split('.');

    // Handles EOB.adjudication[x]
    if (res.length == 1){
      adjIndex = parseInt(res[0].split('[')[1].split(']')[0]);
      adjudication = fhirResource.adjudication[adjIndex]
    }
    // Handles EOB.item[x].adjudication[x]
    else if (res.length == 2) {
      itemIndex = parseInt(res[0].split('[')[1].split(']')[0]);
      adjIndex = parseInt(res[1].split('[')[1].split(']')[0]);
      adjudication = fhirResource.item[itemIndex].adjudication[adjIndex]
    }

    if (!adjudication.category.coding[0].system.endsWith('C4BBAdjudication')) {
        updatedResults.issue.splice(adj.index, 1);
      }
  }

  // check for new error count (error code can be either 'error' or 'fatal')
  const hasErrors = updatedResults.issue.some((obj)=> (obj.severity === 'error' || obj.severity === 'fatal'));
  if (!hasErrors) {
    updatedResults.id = "NoError"
  }

  return updatedResults;
}

/**
 * Returns record whether or not a mapping's value contains code
 * @param {string} value
 * @param {string} path
 * @returns {boolean}
 */
export function isCode(value, path) {
  return value.includes('_.') || /\(*\)/.test(value) || path.includes('@segment:');
}

/**
 * Do basic inclusion check on two string values split on "." character.
 * Check if any values from second string are included in first string.
 *
 * EXAMPLE:
 * If a mapping (e.g. _.period_end_date) contains a value from a rendered resourcePath (e.g. identifier[0].period.end),
 * it's probably ok.
 * @param string1
 * @param string2
 * @returns {*}
 */
export function compareStrings(string1 = '', string2 = '') {
  const string1arr = string1.split('.').map(val => cleanString(val));
  const string2arr = string2.split('.').map(val => cleanString(val));

  return string1arr.some(s1 => string2arr.some(s2 => s1.includes(s2)));
}

/**
 * Replace all numbers, brackets, parens, curly braces, underscores and quotes + lowercase string
 * for comparisons
 * @param str
 * @returns {string}
 */
export function cleanString(str = '') {
  // eslint-disable-next-line
  return str.toString().replaceAll(/[\d\[\](){}_"]+/g, '').toLowerCase()
}

/**
 * Flask generically sends error responses as JSON with a "message"
 * In the node rewrite, we set some error responses as JSON with an "error" property and left some as JSON with a "message" property
 * @param error
 */
export function handleError(error) {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.log(error.response.data);
    console.log(error.response.status);
    console.log(error.response.headers);
    notification.error({
      message: 'Error:',
      description: `${error.response.data.message || error.response.data.error} (${error.response.status})`,
      duration: 6,
    });
  } else {
    notification.error({
      description: 'Error retrieving data from server. Please try again.',
    });
  }
}

/**
 * Send custom notification error message
 * @param msg
 */
export function sendErrorNotification(msg) {
  return notification.error({
    message: 'Error:',
    description: msg,
    duration: 6,
    placement: 'topLeft',
  });
}

export function isAdmin(user_role) {
  return !!(
    (user_role && user_role.includes('admin')) ||
    process.env.REACT_APP_HOST.includes('localhost')
  );
}

export function checkPermission(userPermissions, requiredPermission) {
  if (userPermissions.length) {
    return userPermissions.some(permission => permission.name === requiredPermission);
  } else {
    return false;
  }
}

export function isDebugMode() {
  return !!localStorage.getItem('DEBUG');
}

/**
 * If a user tries to add a path to a mapping, generate a unique path name to avoid triggering DB constraint error.
 * Do this by replacing the hashtag in the template path (e.g. [#]) with a number 1 larger than any existing paths.
 * Use the root node of all templates for comparison to existing mappings. See tests for examples
 * @param path
 * @param mappings
 */
export function getUniquePathName(path, mappings) {
  const paths = path.split('.');

  // check if root path will unflatten to array or obj. if array, find number of child objects
  if (paths[0].includes('[')) {
    const root = paths[0].replace('[#]', '');
    let totalChildCount = 0;
    mappings
      .filter(mapping => mapping.path.includes(root))
      .forEach(mapping => {
        let currentChildNum;

        try {
          currentChildNum = parseInt(mapping.path.match(/(\d+\.?)+/)[0]); // regex to get number in brackets e.g. [1] => 1
        } catch (ignored) {
          return;
        } // if text in brackets is a string instead of num

        if (totalChildCount <= currentChildNum) {
          totalChildCount = currentChildNum + 1;
        }
      });
    return path.replace('#', totalChildCount.toString());
  }

  const found = mappings.filter(mapping => mapping.path === path);

  if (found.length > 0) {
    notification.error({ description: `${path} already exists` });
    return null;
  }

  return path;
}

export function getStickyDropdownSelection(client_id, type, defaultVal) {
  if (JSON.parse(localStorage.getItem(client_id))) {
    const obj = JSON.parse(localStorage.getItem(client_id));

    if (obj[type]) {
      return obj[type];
    }

    return defaultVal;
  }

  return defaultVal;
}

/**
 * Whenever changing  dropdown selection, remember selection so user clicks less
 * @param client_id
 * @param dropdownSelectObj
 */
export function setStickyDropdownSelection(client_id, dropdownSelectObj) {
  localStorage.setItem(client_id, JSON.stringify(dropdownSelectObj));
}

export function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
  notification.success({
    message: 'Copied to clipboard',
    duration: 2,
  });
}

export function downloadFiles(
  sampleData,
  fhirPreview,
  mappingsPrePost,
  resourcetype,
  version,
  file,
  namespace = null,
  sampleFilename = null,
) {
  const zip = new JSZip();

  if (sampleData === null) {
    notification.error({ description: 'Error: no sample data found' });
    return;
  }

  if (fhirPreview.loading) {
    notification.error({ description: 'Error: FHIR resource is loading' });
    return;
  }

  const date = new Date().toISOString().slice(0, 10);
  // if generating in bulk, it is impossible to determine namespace for multiple resources generated of the same type so omit
  const mappingsFilename = namespace
    ? `${file}_${resourcetype}_${namespace}_${version}_${date}`
    : `${file}_${resourcetype}_${version}_${date}`;
  sampleFilename = sampleFilename
    ? `${sampleFilename}.csv`
    : `SAMPLEDATA_${resourcetype}_${date}.csv`;
  const sampleDataCSV = JSONtoCSV([sampleData]);
  const cleanedPrePostMappings = mappingsPrePost.map(record => {
    return {
      path: record.path,
      transformed: record.value,
      pretransform: record.mapping,
    };
  });
  const prePostMappings = JSONtoCSV(cleanedPrePostMappings);

  zip.file(`MAPPINGS_${mappingsFilename}.csv`, new Blob([prePostMappings], { type: 'text/csv' }));
  zip.file(sampleFilename, new Blob([sampleDataCSV], { type: 'text/csv' }));
  zip.file(
    `FHIR_${resourcetype}_${date}.json`,
    new Blob([JSON.stringify(fhirPreview, null, 2)], { type: 'application/json' }),
  );
  zip.generateAsync({ type: 'blob' }).then(content => {
    saveAs(content, `${mappingsFilename}.zip`);
  });
}

export async function exportAllMappings(
  client,
  version = 'latest',
  resourcetype = '*',
  file = '*',
) {
  const res = await _getMappingData(client.id, version, resourcetype, null, file).catch(err =>
    handleError(err),
  );
  await logAction({
    client_id: client.id,
    action: 'Export All Mappings',
    version,
    resourcetype,
    namespace: 'default',
    file,
  }).catch(err => handleError(err));
  const allMappingsCSV = JSONtoCSV(res.data.mappings);
  const filename = `all_fhir_mappings_${client.name}_${version}_${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;
  saveAs(new Blob([allMappingsCSV], { type: 'text/csv' }), filename);
  notification.success({
    description: `Downloaded all mappings for ${client.name} to CSV`,
    duration: 3,
  });
}

/**
 * Log user action to db
 */
export function logAction(logObject) {
  return API.post('/actionLog', logObject);
}

/**
 * Get list of dima users
 */
export async function getDIMAUsers() {
  const res = await API.get('/user');
  return res.data.users;
}

/**
 * Formate date to string
 * ex: May 10, 4:23 AM
 */
export function formatDate(d) {
    const options = {
        hour12: true,
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
    };
    try {
        return (new Date(Number(d)).toLocaleString('en-US', options))
    } catch {
        return "";
    }
}

/**
 * Look up an item in an object using an array.
 * e.g. if obj = { 'a': { 'b': 3 } }
 * then we can look up element using array ['a', 'b'].
 * Value to return here would be 3
 */
export function lookupObjectFromArray(obj, arr) {
  let result = obj;

  for (let i = 0; i < arr.length; i++) {
    result = result[arr[i]];
  }

  return result;
}

/**
 * Get client ID of template.
 * Might build this into separate API routes later.
 */
export async function getTemplateClientId() {
  const clientResponse = await API.get('/client');
  return clientResponse.data.clients.filter(d => d.name === 'TEMPLATE')[0].id;
}

/**
 * Decode HTML entities
 */
export function decodeEntities(str) {
  const element = document.createElement('textarea');
  element.innerHTML = str;
  return element.value;
}

/**
 * Checks if key in dictionary contains
 * a specific value.
 */
export function containsObject(obj, value, key) {
  for (const i in obj) {
    if (obj[i][key] === value) {
      return true;
    }
  }

  return false;
}

export function createColumnHeaders(sampleJson) {
    const columnHeaders = Object.keys(sampleJson[0]).map(d => {
      return {
        title: d,
        dataIndex: d,
        key: d,
        ellipsis: true,
      };
    });
    columnHeaders.unshift({
      title: 'Copy',
      dataIndex: null,
      key: 'null',
      render: (_, record) => {
        return (
          <button
            className="copy-sample-data"
            type="button"
            onMouseDown={() => copyToClipboard(JSON.stringify(record, null, 2))}
          >
            <CopyOutlined />
          </button>
        );
      },
    });
    return columnHeaders;
}

export function createSampleDataRows(sampleJson) {
    const rows = [];

    for (let i = 0; i < sampleJson.length; i += 1) {
      const newRow = {};
      Object.keys(sampleJson[i]).forEach(k => {
        newRow[k] = sampleJson[i][k];
      });
      newRow.key = i;
      rows.push(newRow);
    }

    return rows;
}

/**
 * Get all (non-deleted) clients
 */
export async function getCurrentClients() {
  const clientResponse = await API.get('/client');
  return clientResponse.data.clients.filter((client) => client.deleted === false);
}

/**
 * Get systems from common codesets API.
 */
export async function getCodesetsSystems() {
  const resp = await axios({
    method: "get",
    url: process.env.REACT_APP_COMMON_CODESETS_API_URL+"systems"
  }).catch (function (error) {
    if (error.response) {
      throw new Error(error.response.status.toString());
    }
  })
  const systems = resp.data.systems.map((sys) => sys.name);
  systems.sort();
  return systems;
}

/**
 * Lookup given code from the given system.
 * @param {*} code 
 * @param {*} system 
 * @returns 
 */
export async function lookupCodeFromCodesets(system, code) {
  const resp = await axios({
    method: "get",
    url: process.env.REACT_APP_COMMON_CODESETS_API_URL+`systems/${system}/${code}`
  }).catch( function (error) {
    if (error.response) {
      if (error.response.status === 404) return { data: '{}'} // in case of 404, no match was found.
      throw new Error(error.response.status.toString());
    }
  })
  return resp.data;
}

/**
 * Returns mapping review data
 * @param {string} mappingReviewId
 * @param {string} checklistType
 */
 export async function getReview(mappingReviewId) {
  const res = await API.get(`/mapping/review?mappingReviewId=${mappingReviewId}`).catch(() => {
    sendErrorNotification('There was an error getting the mapping review.');
    return null;
  });
  return res.data;
};


/**
 * Returns mapping review checklist
 * @param {string} mappingReviewId
 * @param {string} checklistType
 */
 export async function getChecklist(mappingReviewId, checklistType) {
  const res = await API.get(
    `/mapping/review/checklist?mappingReviewId=${mappingReviewId}&type=${checklistType}`,
  ).catch(() => {
    sendErrorNotification('There was an error getting the mapping review checklist.');
    return null;
  });
  return res.data.checklist;
};

/**
 * Returns an array mapping review comments
 * @param {string} mappingReviewId
 * @param {string} checklistType
 */
export async function getComments(mappingReviewId,checklistType) {
  // use checklistType to select * reviews where review_id = mappingReviewId AND review_type = checklistType
  const res = await API.get(
    `/mapping/review/comments?mappingReviewId=${mappingReviewId}&type=${checklistType}`,
  ).catch(() => {
    sendErrorNotification(
      `There was an error retrieving ${checklistType} comments for this mapping review`,
    );
    return null;
  });
  return res.data.comments;
};

/**
 * Returns true if mapping review comment successfully updated.
 *  Else returns false and raises error
 * @param {string} mappingReviewId
 * @param {string} checklistType
 * @param {string} currComment
 */
export async function patchComment(mappingReviewId, type, comment, commentId) {
  await API.patch('/mapping/review/comments', {
    mappingReviewId,
    commentId,
    comment,
    type,
    updatedAt: new Date(),
  }).catch(() => {
    sendErrorNotification(`There was an error updating your comment, '${comment}'`);
    return false;
  });
  return true;
};

/**
 * Returns true if mapping review comment successfully posted.
 *  Else returns false and raises error
 * @param {string} mappingReviewId
 * @param {string} checklistType
 * @param {string} currComment
 */
export async function postComment(mappingReviewId, type, comment, checkboxId='00000000-0000-0000-0000-000000000000') {
  // ensures comment is not blank
  if (comment.replace(/(\r\n|\n|\r' ')/gm, '') !== '' && !!/\S/.test(comment)) {
    await API.post('/mapping/review/comments', {
      mappingReviewId,
      comment,
      type,
      checkboxId,
    }).catch(() => {
      sendErrorNotification(`There was an error submitting your comment, '${comment}'`);
      return false;
    });
    return true;
  }
};

/**
 * Returns true if mapping review comment successfully deleted.
 *  Else returns false and raises error
 * @param {string} id
 */
export async function deleteComment(id) {
  await API.delete(`/mapping/review/comments?id=${id}`).catch(() => {
    sendErrorNotification('There was an error deleting your comment.');
    return false;
  });
  return true;
};

/**
 * Returns true if mapping review comment is not blank/whitespace only.
 *  Else returns false and raises error
 * @param {string} comment
 */
export function commentIsNotBlank(comment) {
  // ensures comment is not blank
  if (comment.replace(/(\r\n|\n|\r' ')/gm, '') !== '' && !!/\S/.test(comment)) {
    return true;
  };
  return false;
};

/**
 * Returns a list of structuredef profile urls currently in the db
 *  Else returns null and raises error
 */
export async function getStructureDefProfiles() {
  const res = await API.get('/structuredef/profiles').catch(() => {
    sendErrorNotification('There was an error retrieving Stucture Definition Profiles.');
      return null;
    });
  return res.data.profiles;
};

/**
 * Returns structuredef information for a given profile url
 *  Else returns null and raises error
 * @param {string} profileUrl
 */
export async function getStructureDefProfileUrl(profileUrl) {
  const url = profileUrl.split('|')[0]
  const res = await API.get(`/structuredef?profile_url=${url}`).catch(() => {
    sendErrorNotification(`There was an error retrieving the Stucture Definition Profile form URL: ${profileUrl}`);
      return null;
    });
  return res.data;
};

/**
 * Create item to be added to menu
 * @param {Object} itemLabel displayed label for the menu item
 * @param {String} key
 * @param {Object} icon AntD icon
 * @param {String} path
 * @param {Object} style
 */
export function createMenuItem(itemLabel, key, icon, path, style) {
  let label = itemLabel;
  // if path is given, then the menu item is a link to the path.
  if (path !== undefined) {
    label = <Link to={path}>
      {label}
    </Link>
  }
  return {
    label,
    key,
    icon,
    style
  };
}

/*
 * Gets implementation guide based on the meta.profile.
 * @param {list} dataSource
 */
export function getImplementationGuide(dataSource) {
  const META_PATH = 'meta.profile[0]';
  const metaMapping = dataSource.find(mapping => mapping.path === META_PATH);

  if (metaMapping) {
    return metaMapping.value.replaceAll('"', '');
  }

  return null;
};

/**
 * Logs a custom event to Google Analytics
 * @param {string} eventName
 * @param {object} options
 */
export const logGAEvent = (eventName, options = null) => {
  options ? gtag('event', eventName, options) : gtag('event', eventName);
};

/**
 * Makes a request to get a list of the current users' preferences
 */
 export const fetchUserPreferences = async () => {
  try {
      const {
          data: { preferences },
      } = await API.get('/user/current/preferences');
      return preferences;    
  } catch (e) {
    console.log(e);
      return [];
  }
}

/**
 * Filter user preferences for only a specific preference type
 */
 export const filterUserPreferences = (preferenceType, userPreferencesList) => {
  return userPreferencesList.filter((up) => up.preference_type === preferenceType)
}

/**
 * Sets filesize to human readable value
 */
export const displayFileSize = size => {
  const i = size === '0' ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return `${parseFloat((size / 1024 ** i).toFixed(2))} ${['B', 'KB', 'MB', 'GB', 'TB'][i]}`;
};

export const sortArrOfObjectsByStr = (arr, key) => {
  arr.sort((a,b) => a[key].localeCompare(b[key]));
  return arr;
}
